/**
 * components/legal/vop/vopPriceList.ts
 *
 * The price list (Ceník): Annex 3 of the general terms, as counsel drafted it
 * on 27 Oct 2026, served at /subscription.
 *
 * It is a contract document, and counsel asked that its content be neither
 * reworded for marketing nor added to: the page may present it graphically,
 * but says nothing it does not. So the page is the text alone. The price
 * sheet with its examples and euro guide stays on the welcome page
 * (`pricelist/PriceListPlans.tsx`), which may advertise as long as it agrees
 * with this; how to pay and how the service is delivered, which the payment
 * gateway reads, are on a page of their own (`buildVopPayment`).
 *
 * Every figure is the product's: the prices, the cadence and the horizon from
 * `priceList.ts` (held to the billing engine by `priceList.parity.test.ts`),
 * the trial from {@link VOP_FIGURES} (held to the service by
 * `specification.parity.test.ts`). The Czech is counsel's; the English is a
 * translation.
 */

import type { LegalDoc } from "../legalContent";
import { PRICE_LIST_PLAN_NAMES, priceListPlan } from "../priceList";
import { vopEffectiveDate } from "./effectiveDate";
import { VOP_FIGURES } from "./vopSpecification";

type Language = "cs" | "en";

const grouped = (value: number, separator: string): string =>
    String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

/** "2 990 Kč" in Czech, "2,990 CZK" in English. */
function amount(lang: Language, czk: number): string {
    return lang === "cs" ? `${grouped(czk, " ")} Kč` : `${grouped(czk, ",")} CZK`;
}

const hotels = priceListPlan("pro");
const rentals = priceListPlan("rentals");
if (!hotels || !rentals) throw new Error("the price list names no OPTIVO Hotels or OPTIVO Rentals");

const { pro: HOTELS, rentals: RENTALS } = PRICE_LIST_PLAN_NAMES;

export const VOP_PRICE_LIST_CS: LegalDoc = {
    title: "Ceník",
    effectiveDate: vopEffectiveDate("cs"),
    intro: ["Příloha č. 3 všeobecných obchodních podmínek."],
    sections: [
        {
            id: "introductory",
            heading: "1. Úvodní ustanovení",
            clauses: [
                { text: "Ceník stanoví Tarify, kritéria jejich určení, výši Odměny a způsob jejího výpočtu. Pojmy s velkým počátečním písmenem mají význam stanovený ve VOP." },
                { text: "Ceny jsou uvedeny v korunách českých a Odměna se účtuje v korunách českých. Poskytovatel není plátcem daně z přidané hodnoty; uvedené ceny jsou konečné. Stane-li se Poskytovatel plátcem daně z přidané hodnoty, připočte se k cenám daň v zákonné výši." },
                { text: "Zřizovací poplatek se neúčtuje." },
            ],
        },
        {
            id: "plans",
            heading: "2. Tarify a jejich určení",
            clauses: [
                {
                    text: "Tarif určuje Služba pro každý Objekt podle jeho typu takto:",
                    items: [
                        `${RENTALS} – penzion, apartmán, rekreační pronájem a kemp;`,
                        `${HOTELS} – každý jiný Objekt, zejména hotel, resort a hostel.`,
                    ],
                },
                { text: "Tarif se určuje bez ohledu na počet ubytovacích jednotek Objektu. Uživatel může mít v Účtu Objekty zařazené do různých Tarifů." },
                { text: `Typ Objektu se určuje podle údaje, který Uživatel uvede při zařazení Objektu do Služby. Dokud typ uveden není, platí pro Objekt Tarif ${HOTELS}.` },
            ],
        },
        {
            id: "fee",
            heading: "3. Odměna",
            clauses: [
                {
                    text: "Měsíční Odměna za Objekt činí:",
                    items: [
                        `v Tarifu ${HOTELS} ${amount("cs", hotels.propertyMonthlyCzk)} za Objekt a ${amount("cs", hotels.roomMonthlyCzk)} za každou zpoplatněnou ubytovací jednotku;`,
                        `v Tarifu ${RENTALS} ${amount("cs", rentals.propertyMonthlyCzk)} za Objekt a ${amount("cs", rentals.roomMonthlyCzk)} za každou zpoplatněnou ubytovací jednotku.`,
                    ],
                },
                { text: "Každý Objekt se zpoplatňuje samostatně podle svého Tarifu a počtu svých zpoplatněných ubytovacích jednotek. Objekt bez zpoplatněné ubytovací jednotky se nezpoplatňuje." },
                { text: "Zpoplatněnou ubytovací jednotkou je pokoj nebo jiná ubytovací jednotka, například apartmán, pro kterou Služba počítá Vypočtenou cenu. Ubytovací jednotky, které Uživatel z výpočtu vyřadí, se nezpoplatňují." },
                { text: "Počet zpoplatněných ubytovacích jednotek se určuje podle stavu ke dni vystavení platby za Předplatné období." },
            ],
        },
        {
            id: "scope",
            heading: "4. Rozsah Tarifů",
            clauses: [
                { text: `Oba Tarify zahrnují funkcionality Služby v rozsahu Specifikace. Vypočtená cena se počítá v horizontu až ${hotels.horizonDays} nocí; četnost přepočtu nastavuje Poskytovatel pro každý Objekt, nejvýše ${hotels.uploadsPerDay}× denně.` },
                { text: "Součástí obou Tarifů je jednou za čtvrtletí přehled strategie, tedy automaticky vytvořený přehled nastavení Strategie Objektu s Automatizovanými návrhy jeho úprav. Pracovník Poskytovatele přehled před zpřístupněním kontroluje z hlediska úplnosti a technické správnosti, nikoli jeho obchodní vhodnosti." },
                { text: "Asistenta Optiva lze užívat v přiměřeném rozsahu. Poskytovatel může stanovit limit jeho užívání; aktuální limit je uveden v Portálu." },
                { text: "Další přehledy strategie nad rámec jednoho za čtvrtletí a rozšířené sledování veřejných cen lze sjednat individuálně; Odměna za ně se sjednává individuálně." },
            ],
        },
        {
            id: "trial",
            heading: "5. Zkušební období",
            clauses: [
                { text: `Poskytovatel může Uživateli po schválení Účtu poskytnout bezplatné zkušební období v délce až ${VOP_FIGURES.trialDays} dnů (dále jen „Zkušební období“). O poskytnutí, délce a prodloužení Zkušebního období rozhoduje Poskytovatel; na ně není nárok. Za Zkušební období se Odměna neúčtuje a platební údaje se nevyžadují.` },
                { text: "Po uplynutí Zkušebního období se funkcionality Služby zpřístupní uhrazením Odměny za Předplatné období; Odměna se automaticky nestrhává." },
                { text: "Poskytovatel může Zkušební období jednou prodloužit o jeden měsíc, zejména po uplatnění promo kódu v Účtu; na prodloužení není nárok. Zkušební období lze pro tentýž Objekt využít jen jednou." },
            ],
        },
        {
            id: "closing",
            heading: "6. Závěrečné ustanovení",
            clauses: [{ text: "Způsob úhrady, automatické obnovování Předplatného období a skončení Smlouvy se řídí VOP." }],
        },
    ],
};

export const VOP_PRICE_LIST_EN: LegalDoc = {
    title: "Price List",
    effectiveDate: vopEffectiveDate("en"),
    intro: ["Annex No. 3 to the general terms and conditions. This English text is a translation; the Czech version is binding."],
    sections: [
        {
            id: "introductory",
            heading: "1. Introductory provisions",
            clauses: [
                { text: "The Price List lays down the Plans, the criteria by which they are determined, the amount of the Fee and how it is calculated. Capitalised terms have the meaning given in the GTC." },
                { text: "Prices are stated in Czech crowns and the Fee is charged in Czech crowns. The Provider is not a VAT payer; the prices stated are final. Should the Provider become a VAT payer, VAT at the statutory rate will be added to the prices." },
                { text: "No setup fee is charged." },
            ],
        },
        {
            id: "plans",
            heading: "2. Plans and how they are determined",
            clauses: [
                {
                    text: "The Service determines the Plan for each Property by its type, as follows:",
                    items: [
                        `${RENTALS} – a guesthouse, an apartment, a vacation rental and a campsite;`,
                        `${HOTELS} – any other Property, in particular a hotel, a resort and a hostel.`,
                    ],
                },
                { text: "The Plan is determined regardless of the number of accommodation units of the Property. The User may have Properties assigned to different Plans in its Account." },
                { text: `The type of a Property is determined by the information the User states when adding the Property to the Service. Until the type is stated, the ${HOTELS} Plan applies to the Property.` },
            ],
        },
        {
            id: "fee",
            heading: "3. The Fee",
            clauses: [
                {
                    text: "The monthly Fee for a Property is:",
                    items: [
                        `on the ${HOTELS} Plan, ${amount("en", hotels.propertyMonthlyCzk)} per Property and ${amount("en", hotels.roomMonthlyCzk)} for each charged accommodation unit;`,
                        `on the ${RENTALS} Plan, ${amount("en", rentals.propertyMonthlyCzk)} per Property and ${amount("en", rentals.roomMonthlyCzk)} for each charged accommodation unit.`,
                    ],
                },
                { text: "Each Property is charged separately according to its Plan and the number of its charged accommodation units. A Property with no charged accommodation unit is not charged." },
                { text: "A charged accommodation unit is a room or other accommodation unit, for example an apartment, for which the Service calculates a Calculated Price. Accommodation units the User excludes from the calculation are not charged." },
                { text: "The number of charged accommodation units is determined as it stands on the day the payment for the Subscription Period is issued." },
            ],
        },
        {
            id: "scope",
            heading: "4. Scope of the Plans",
            clauses: [
                { text: `Both Plans include the functionalities of the Service to the extent of the Specification. The Calculated Price is calculated over a horizon of up to ${hotels.horizonDays} nights; the frequency of recalculation is set by the Provider for each Property, up to ${hotels.uploadsPerDay}× a day.` },
                { text: "Both Plans include a strategy overview once a quarter, that is, an automatically created overview of the settings of the Property's Strategy with Automated Proposals for adjusting them. A member of the Provider's staff checks the overview for completeness and technical correctness before it is made available, not for its commercial suitability." },
                { text: "The Optivo assistant may be used to a reasonable extent. The Provider may set a limit on its use; the current limit is stated in the Portal." },
                { text: "Further strategy overviews beyond one a quarter and extended monitoring of public prices may be agreed individually; the Fee for them is agreed individually." },
            ],
        },
        {
            id: "trial",
            heading: "5. Trial Period",
            clauses: [
                { text: `After approving the Account, the Provider may grant the User a free trial period of up to ${VOP_FIGURES.trialDays} days (the "Trial Period"). Whether a Trial Period is granted, how long it lasts and whether it is extended is decided by the Provider; there is no entitlement to it. No Fee is charged for the Trial Period and no payment details are required.` },
                { text: "After the Trial Period ends, the functionalities of the Service become available once the Fee for a Subscription Period is paid; the Fee is not charged automatically." },
                { text: "The Provider may extend the Trial Period once by one month, in particular when a promo code is applied in the Account; there is no entitlement to the extension. A Trial Period may be used only once for the same Property." },
            ],
        },
        {
            id: "closing",
            heading: "6. Final provision",
            clauses: [{ text: "The method of payment, the automatic renewal of the Subscription Period and the end of the Contract are governed by the GTC." }],
        },
    ],
};
